<script setup lang="ts">
import { computed } from 'vue'
import { CDialog } from '@/shared/ui'

interface ServiceDetailsDialogProps {
  modelValue: boolean
  iconClass: string
  title: string
  price?: string
  bodyTitle?: string
  subdescription?: string
  listTitle?: string
  listDescription?: string
  listItems?: string[]
  downloadButtonText?: string
}

interface ServiceDetailsDialogEmits {
  'update:modelValue': [value: boolean]
  'download': []
}

const props = defineProps<ServiceDetailsDialogProps>()
const emit = defineEmits<ServiceDetailsDialogEmits>()

const dialogModel = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
})
</script>

<template>
  <CDialog
    v-model="dialogModel"
    :title="title"
    class="max-w-[480px] !w-[90%] sm:!w-[480px]"
  >
    <template #header>
      <div class="flex items-center justify-between w-full">
        <div class="flex items-center gap-4">
          <div
            class="w-12 h-12 bg-primary-light-9 rounded-xl flex items-center justify-center"
          >
            <i :class="iconClass" class="text-[32px] text-brand-primary" />
          </div>
          <div>
            <h2 class="text-lg font-semibold leading-7 text-text-primary">
              {{ title }}
            </h2>
            <p v-if="price" class="text-sm text-text-secondary mt-1">
              {{ price }}
            </p>
          </div>
        </div>
        <button
          class="text-gray-400 hover:text-gray-600 transition-colors"
          @click="$emit('update:modelValue', false)"
        >
          <i class="icon-close text-xl" />
        </button>
      </div>
    </template>

    <div>
      <!-- Body Title and List -->
      <div v-if="bodyTitle || listItems?.length" class="space-y-2 mb-3">
        <h3
          v-if="bodyTitle"
          class="font-medium text-text-primary text-sm leading-4"
        >
          {{ bodyTitle }}
        </h3>
        <p
          v-if="subdescription"
          class="text-sm text-text-regular font-medium leading-none"
        >
          {{ subdescription }}
        </p>
        <p
          v-if="listTitle"
          class="text-sm text-text-regular font-medium leading-none !mt-4"
        >
          {{ listTitle }}
        </p>
        <ul v-if="listItems?.length" class="space-y-3">
          <li
            v-for="(item, index) in listItems"
            :key="index"
            class="flex items-center gap-3"
          >
            <div
              class="w-8 h-8 bg-primary-light-9 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
            >
              <span class="text-sm font-medium text-brand-primary">{{
                index + 1
              }}</span>
            </div>
            <p
              class="text-text-secondary text-xs text-left font-normal leading-4"
            >
              {{ item }}
            </p>
          </li>
        </ul>
      </div>

      <!-- Download Button -->
      <!-- TODO: Временно скрыт по запросу клиента
      <div v-if="downloadButtonText" class="flex justify-center mb-5">
        <CButton
          type="primary"
          class="bg-brand-violet hover:bg-brand-violet-dark text-white px-6 py-3 w-full"
          @click="$emit('download')"
        >
          <i class="icon-hugeicons_download-04 text-2xl mr-2"></i>
          {{ downloadButtonText }}
        </CButton>
      </div>
      -->
      <p
        v-if="listDescription"
        class="text-sm text-text-regular font-medium leading-none"
      >
        {{ listDescription }}
      </p>
      <!-- Form Slot -->
      <slot name="form" />
    </div>

    <!-- Footer Actions Slot -->
    <template #footer>
      <slot name="footer-actions" />
    </template>
  </CDialog>
</template>
